import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import brainIcon from '../assets/images/icon/brain_icon.png';
import '../styles/floating-competition-button.css';
import { useIsMobile } from '../hooks/useIsMobile';

/**
 * FloatingCompetitionButton - A floating action button for quick access to Competition/Class rankings
 *
 * Design Philosophy:
 * - Parallel to the AI Assistant button on the opposite side
 * - Cyan brain icon representing the "smart competition" aspect
 * - Provides instant 1-tap access to the most engaging feature (rankings)
 * - Shows visual feedback when user's rank changes
 * - Mobile-first design with bold, game-like aesthetics
 */
const FloatingCompetitionButton = ({ userRank, totalParticipants, hasNewActivity }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isAnimating, setIsAnimating] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const isMobile = useIsMobile();


  // Don't show on competition page itself
  if (location.pathname === '/groups' || location.pathname.startsWith('/groups/')) {
    return null;
  }

  // Don't show if not logged in
  if (!user) {
    return null;
  }

  const handleClick = () => {
    setIsAnimating(true);

    // Haptic feedback on mobile
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }

    setTimeout(() => {
      setIsAnimating(false);
      navigate('/groups');
    }, 200);
  };

  // Format rank display
  const getRankDisplay = () => {
    if (!userRank) return null;

    // Add ordinal suffix
    const getOrdinal = (n) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    return getOrdinal(userRank);
  };

  const rankDisplay = getRankDisplay();

  return (
    <div
      className={`floating-competition-button ${isAnimating ? 'animating' : ''} ${hasNewActivity ? 'has-activity' : ''}`}
      onClick={handleClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      role="button"
      tabIndex={0}
      aria-label="Go to Competition Rankings"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Main Button */}
      <div className="competition-icon-container">
        <div className="competition-icon brain-icon">
          <img src={brainIcon} alt="Class" className="brain-svg" />
        </div>

        {/* Rank Badge (if available) */}
        {rankDisplay && (
          <div className="rank-badge">
            <span className="rank-hash">#</span>
            <span className="rank-number">{userRank}</span>
          </div>
        )}

        {/* Activity Indicator */}
        {hasNewActivity && (
          <div className="activity-indicator">
            <span className="activity-dot"></span>
          </div>
        )}
      </div>

      {/* Label */}
      <span className="competition-label">CLASS</span>

      {/* Tooltip (desktop only) */}
      {!isMobile && showTooltip && (
        <div className="competition-tooltip">
          <div className="tooltip-content">
            <span className="tooltip-title">Class Rankings</span>
            {rankDisplay && (
              <span className="tooltip-rank">You're {rankDisplay}!</span>
            )}
            {totalParticipants && (
              <span className="tooltip-participants">of {totalParticipants} students</span>
            )}
          </div>
          <div className="tooltip-arrow"></div>
        </div>
      )}

      {/* Pulse Ring Animation */}
      <div className="pulse-ring"></div>
      <div className="pulse-ring delay-1"></div>
    </div>
  );
};

export default FloatingCompetitionButton;
